export type MemberRoleValue =
  | "company_admin"
  | "company_recruiter"
  | "company_hiring_manager"

export interface MemberRoleOption {
  value: MemberRoleValue
  label: string
}

export function getMemberRoleLabel(role: string): string {
  switch (role) {
    case "company_admin":
      return "Admin"
    case "company_recruiter":
      return "Recruiter"
    case "company_hiring_manager":
      return "Hiring Manager"
    default:
      return role
  }
}

export interface ApiCompanyMember {
  id: number
  email: string
  first_name: string
  middle_name: string | null
  last_name: string
  group: MemberRoleValue
  groups?: string[]
  designation: string
  gender: "M" | "F" | null
  contact_number: string | null
  company: {
    id: number
    name: string
    profile_picture: string | null
    website: string | null
    email: string | null
    contact_number: string | null
    industry: string | null
    country: string | null
    city: string | null
    address_line_1: string | null
    address_line_2: string | null
    about: string | null
  }
  department: {
    id: number
    name: string
  } | null
  // Backend may return either a numeric ID or a FileAttachment object
  profile_picture: number | null | { id: number; [key: string]: any }
}

export interface CompanyMemberListResponse {
  message: string
  code: number
  data: ApiCompanyMember[] | ApiCompanyMember
  error: Record<string, unknown>
}

export interface CompanyMemberResponse {
  message: string
  code: number
  data: ApiCompanyMember
  error: Record<string, unknown>
}

interface MemberRolesResponse {
  message: string
  code: number
  data: string[]
  error: Record<string, unknown>
}

// Helper function to make authenticated API calls
async function authenticatedApiCall(url: string, options: RequestInit = {}) {
  const { authenticatedFetch } = await import("../auth/apiInterceptor")
  return authenticatedFetch(url, options)
}

export async function fetchCompanyMemberList(): Promise<{
  data?: ApiCompanyMember[]
  error?: string
}> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/list/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "GET",
    })

    const responseBody: CompanyMemberListResponse = await response.json().catch(() => null)

    if (!response.ok) {
      return { error: responseBody?.message || "Failed to fetch company members." }
    }

    // Ensure data is always an array
    const data = responseBody?.data
    const dataArray = Array.isArray(data) ? data : data ? [data] : []
    return { data: dataArray }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to fetch company members.",
    }
  }
}

export async function fetchMemberRoles(): Promise<{
  data?: MemberRoleOption[]
  error?: string
}> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/role/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "GET",
    })

    const responseBody: MemberRolesResponse = await response.json().catch(() => null)

    if (!response.ok) {
      return { error: responseBody?.message || "Failed to fetch member roles." }
    }

    return {
      data: (responseBody?.data ?? []).map((role) => ({
        value: role as MemberRoleValue,
        label: getMemberRoleLabel(role),
      })),
    }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to fetch member roles.",
    }
  }
}

export interface CreateCompanyMemberPayload {
  group?: MemberRoleValue
  groups?: string[]
  email: string
  first_name: string
  middle_name: string
  last_name: string
  designation: string
  gender: string
  contact_number?: string | null
  company: number
  department: number
  profile_picture?: number | null
}

export async function createCompanyMember(
  payload: CreateCompanyMemberPayload
): Promise<{ data?: ApiCompanyMember; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/create/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })

    const responseBody: CompanyMemberResponse = await response.json().catch(() => null)

    if (!response.ok) {
      return { error: responseBody?.message || "Failed to create company member." }
    }

    return { data: responseBody?.data }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to create company member.",
    }
  }
}

export async function fetchCompanyMemberById(
  memberId: number
): Promise<{ data?: ApiCompanyMember; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/${memberId}/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "GET",
    })

    const responseBody: CompanyMemberResponse = await response.json().catch(() => null)

    if (!response.ok) {
      return { error: responseBody?.message || "Failed to fetch company member." }
    }

    return { data: responseBody?.data }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to fetch company member.",
    }
  }
}

export interface UpdateCompanyMemberPayload {
  first_name: string
  middle_name: string
  last_name: string
  designation: string
  gender: string
  department: number
  contact_number?: string | null
  profile_picture?: number | null
  group?: MemberRoleValue
  groups?: string[]
}

export async function updateCompanyMember(
  memberId: number,
  payload: UpdateCompanyMemberPayload
): Promise<{ data?: ApiCompanyMember; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/${memberId}/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })

    const responseBody: CompanyMemberResponse = await response.json().catch(() => null)

    if (!response.ok) {
      return { error: responseBody?.message || "Failed to update company member." }
    }

    return { data: responseBody?.data }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to update company member.",
    }
  }
}

export async function deleteCompanyMember(
  memberId: number
): Promise<{ success?: boolean; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const endpoint = `${API_BASE_URL}/api/company/member/${memberId}/`

  try {
    const response = await authenticatedApiCall(endpoint, {
      method: "DELETE",
    })

    if (!response.ok) {
      const responseBody = await response.json().catch(() => null)
      const errorMessage =
        (responseBody && typeof responseBody === "object" && "message" in responseBody
          ? String(responseBody.message)
          : null) || "Failed to delete company member."

      return { error: errorMessage }
    }

    return { success: true }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to delete company member.",
    }
  }
}

